import { computed, Injectable, signal } from '@angular/core';
import { QUEUE } from '../services/mock-data';
import type { QueueCategory, QueueItem } from '../models/queue.model';

export type QueueFilter = QueueCategory | 'all';
export type QueueSort = 'ai-priority' | 'premium' | 'confidence' | 'account';

export interface QueueGroup {
  category: QueueCategory;
  label: string;
  items: QueueItem[];
}

@Injectable({ providedIn: 'root' })
export class QueueStore {
  readonly items = signal<QueueItem[]>(QUEUE);
  readonly filter = signal<QueueFilter>('all');
  readonly search = signal('');
  readonly sort = signal<QueueSort>('ai-priority');
  readonly activeId = signal<string | null>(null);

  readonly filtered = computed(() => {
    const q = this.search().trim().toLowerCase();
    const base = this.items().filter((item) => {
      const matchesFilter = this.filter() === 'all' || item.category === this.filter();
      const matchesSearch =
        !q ||
        item.accountName.toLowerCase().includes(q) ||
        item.product.toLowerCase().includes(q) ||
        item.broker.toLowerCase().includes(q);
      return matchesFilter && matchesSearch;
    });

    const sortKey = this.sort();
    return [...base].sort((a, b) => {
      switch (sortKey) {
        case 'premium':
          return b.premium - a.premium;
        case 'confidence':
          return b.confidence - a.confidence;
        case 'account':
          return a.accountName.localeCompare(b.accountName);
        default:
          return a.aiPriority - b.aiPriority;
      }
    });
  });

  readonly groups = computed<QueueGroup[]>(() => {
    const order: QueueCategory[] = ['high', 'judgment', 'straight-through', 'blocked'];
    const labels: Record<QueueCategory, string> = {
      high: 'High Priority',
      judgment: 'Requires Human Judgment',
      'straight-through': 'Straight-Through Approvals',
      blocked: 'Blocked / Awaiting Broker',
    };
    const filtered = this.filtered();
    return order
      .map((category) => ({
        category,
        label: labels[category],
        items: filtered.filter((i) => i.category === category),
      }))
      .filter((g) => g.items.length > 0 || this.filter() === 'all');
  });

  readonly highPriorityCount = computed(() => this.items().filter((i) => i.category === 'high' || i.category === 'judgment').length);
  readonly straightThroughCount = computed(() => this.items().filter((i) => i.category === 'straight-through').length);
  readonly blockedCount = computed(() => this.items().filter((i) => i.category === 'blocked').length);

  setFilter(filter: QueueFilter): void {
    this.filter.set(filter);
  }

  setSearch(search: string): void {
    this.search.set(search);
  }

  setSort(sort: QueueSort): void {
    this.sort.set(sort);
  }

  setActive(id: string | null): void {
    this.activeId.set(id);
  }
}
